// DOKUMEN — invoice & nota in the app (Laguna Terang 1A).
//
// The document is shown inside an <iframe srcdoc> whose HTML is DOCRENDER.renderDocument — the same
// string the public WhatsApp link serves — and Cetak prints that iframe. So screen = paper = link.
// Nothing here draws the document itself; this file is only the chrome around it (toolbar, scaling,
// the "Isi dokumen ini" options for staff who may adjust a document).
const uSdc = React.useState, uEdc = React.useEffect, uRdc = React.useRef;

// Design settings (identity, payment, signature, content, paper) — one cached fetch per session.
let docSettingsP = null;
const loadDocSettings = (force) => {
  if (!docSettingsP || force) {
    docSettingsP = window.API.distribusi.docSettings.get()
      .then((r) => DOCRENDER.mergeDocSettings(r && r.data))
      .catch(() => { docSettingsP = null; return DOCRENDER.mergeDocSettings({}); });
  }
  return docSettingsP;
};

const docPxOf = (mm) => Math.round(mm * 96 / 25.4);
// Server error text verbatim (it is written for the user), else a generic line.
const docErrMsg = (e, fb) => (e && e.body && e.body.error && e.body.error.message) || (e && e.message) || trD(fb || 'doc.saveErr');

// One rendered document, scaled down (never up) to fit its container; height follows the content.
// `highlight` (design screen only) outlines the part being edited — a CSS selector inside the document.
function DocFrame({ doc, settings, frameRef, highlight }) {
  const boxRef = uRdc(null); const ownRef = uRdc(null); const ref = frameRef || ownRef;
  const paper = DOCRENDER.PAPERS[DOCRENDER.paperFor(doc, settings)] || DOCRENDER.PAPERS.a4;
  const paperW = docPxOf(paper.w);
  const [boxW, setBoxW] = uSdc(paperW);
  const [h, setH] = uSdc(paper.h ? docPxOf(paper.h) : 600);
  const html = React.useMemo(() => {
    const h = DOCRENDER.renderDocument(doc, settings, {});
    return highlight ? h.replace('</head>', '<style>' + highlight + '{outline:2px dashed #E8A317;outline-offset:5px;border-radius:12px}</style></head>') : h;
  }, [doc, settings, highlight]);
  uEdc(() => {
    const el = boxRef.current; if (!el) return undefined;
    const set = () => setBoxW(el.clientWidth || paperW); set();
    if (typeof ResizeObserver === 'undefined') { window.addEventListener('resize', set); return () => window.removeEventListener('resize', set); }
    const ro = new ResizeObserver(set); ro.observe(el); return () => ro.disconnect();
  }, [paperW]);
  const measure = () => {
    const f = ref.current; const d = f && f.contentDocument;
    if (d && d.documentElement) setH(Math.max(d.documentElement.scrollHeight, d.body ? d.body.scrollHeight : 0));
  };
  const scale = Math.min(1, boxW / paperW);
  return (
    <div className="doc-frame-box" ref={boxRef}>
      <div className="doc-frame-fit" style={{ width: Math.round(paperW * scale), height: Math.round(h * scale) }}>
        <iframe ref={ref} className="doc-frame" title={(doc && doc.number) || 'dokumen'} srcDoc={html} onLoad={measure}
          sandbox="allow-same-origin allow-modals" style={{ width: paperW, height: h, transform: 'scale(' + scale + ')' }} />
      </div>
    </div>
  );
}

// Prints exactly what the frame shows (its own @page rules set the paper size).
function printDocFrame(frameRef) {
  const f = frameRef && frameRef.current;
  if (!f || !f.contentWindow) return;
  f.contentWindow.focus(); f.contentWindow.print();
}

// "Isi dokumen ini" — per-document options (cap: distribusiDokumenSesuaikan).
function DocOptionsFields({ value, onChange, docType }) {
  const o = DOCRENDER.mergeDocOptions(value);
  const set = (patch) => onChange({ ...o, ...patch });
  const setCol = (k, v) => set({ hideCols: { ...o.hideCols, [k]: v } });
  const rec = docType === 'nota' ? '' : 'a4';
  return (
    <div className="doc-opts">
      <label className="doc-switch">
        <input type="checkbox" checked={!!o.showSisaBon} onChange={(e) => set({ showSisaBon: e.target.checked })} />
        <span className="doc-switch-ui" aria-hidden="true" />
        <span><b>{trD('doc.showSisa')}</b><small>{trD('doc.showSisaHint')}</small></span>
      </label>
      <div className="fld-label">{trD('doc.hideCols')}</div>
      <div className="doc-checks">
        {[['unitPrice', 'doc.colUnit'], ['qty', 'doc.colQty'], ['date', 'doc.colDate']].map(([k, l]) => (
          <label key={k} className="doc-check"><input type="checkbox" checked={!!o.hideCols[k]} onChange={(e) => setCol(k, e.target.checked)} />{trD(l)}</label>
        ))}
      </div>
      <label className="fld-label">{trD('doc.noteOverride')}</label>
      <textarea className="fld" rows={3} maxLength={500} value={o.noteOverride} placeholder={trD('doc.notePh')} onChange={(e) => set({ noteOverride: e.target.value.slice(0, 500) })} />
      <label className="fld-label">{trD('doc.paper')}</label>
      <select className="fld" value={o.paper} onChange={(e) => set({ paper: e.target.value })}>
        <option value="">{trD('doc.paperDefault')}</option>
        {Object.keys(DOCRENDER.PAPERS).map((k) => <option key={k} value={k}>{DOCRENDER.PAPERS[k].label}{k === rec ? ' ' + trD('doc.recommended') : ''}</option>)}
      </select>
    </div>
  );
}

// Full-screen viewer: toolbar (Cetak · Kirim WhatsApp · Ubah isi dokumen) + the document frame.
function InvoiceViewer({ invoice, onClose, canCustomize = false }) {
  const [doc, setDoc] = uSdc(invoice);
  const [settings, setSettings] = uSdc(null);
  const [waOpen, setWaOpen] = uSdc(false);
  const [editing, setEditing] = uSdc(false);
  const [draft, setDraft] = uSdc(null);
  const [busy, setBusy] = uSdc(false);
  const [err, setErr] = uSdc('');
  const frameRef = uRdc(null);
  const isNota = doc.docType === 'nota';
  uEdc(() => { loadDocSettings().then(setSettings); }, []);
  uEdc(() => {
    const o = (e) => { if (e.key === 'Escape' && !waOpen) onClose(); };
    window.addEventListener('keydown', o); return () => window.removeEventListener('keydown', o);
  }, [waOpen]);
  const startEdit = () => { setDraft(DOCRENDER.mergeDocOptions(doc.options)); setErr(''); setEditing(true); };
  const save = () => {
    if (busy) return; setBusy(true); setErr('');
    window.API.distribusi.invoices.setOptions(doc.id, draft)
      .then((r) => { setDoc(r.data); setEditing(false); })
      .catch((e) => setErr(docErrMsg(e)))
      .finally(() => setBusy(false));
  };
  // While editing, the frame previews the draft — what Simpan will store.
  const shown = editing && draft ? { ...doc, options: draft } : doc;
  return (
    <div className="modal-scrim doc-viewer" onClick={onClose} style={{ zIndex: 210 }}>
      <div className="doc-viewer-card" onClick={(e) => e.stopPropagation()}>
        <div className="doc-toolbar">
          <div className="doc-toolbar-t"><b>{trD(isNota ? 'doc.titleNota' : 'doc.titleInvoice')}</b><span>{doc.number}{doc.customer && doc.customer.name ? ' · ' + doc.customer.name : ''}</span></div>
          <div className="doc-toolbar-a">
            <button className="btn btn-primary" disabled={!settings} onClick={() => printDocFrame(frameRef)}><IconDownload s={16} />{trD('doc.print')}</button>
            <button className="btn btn-ghost" onClick={() => setWaOpen(true)}><IconWhatsApp s={16} />{trD('doc.sendWa')}</button>
            {canCustomize && !editing && <button className="btn btn-ghost" onClick={startEdit}><IconPencil s={15} />{trD('doc.customize')}</button>}
          </div>
          <button className="jp-icon doc-close" aria-label={trD('doc.close')} onClick={onClose}><IconClose s={18} /></button>
        </div>
        <div className={'doc-viewer-body' + (editing ? ' editing' : '')}>
          {editing && (
            <div className="doc-opts-panel">
              <div className="doc-opts-h">{trD('doc.optsTitle')}</div>
              <DocOptionsFields value={draft} onChange={setDraft} docType={doc.docType} />
              {err && <div className="dist-warnbox" style={{ marginTop: 10 }}><IconWarn s={14} /><span>{err}</span></div>}
              <div className="doc-opts-foot">
                <button className="btn btn-ghost" disabled={busy} onClick={() => setEditing(false)}>{trD('doc.cancel')}</button>
                <button className="btn btn-primary" disabled={busy} onClick={save}><IconCheck s={15} />{trD('doc.save')}</button>
              </div>
            </div>
          )}
          <div className="doc-viewer-paper">
            {settings ? <DocFrame doc={shown} settings={settings} frameRef={frameRef} /> : <div className="doc-loading"><IconClock s={14} />{trD('doc.loading')}</div>}
          </div>
        </div>
        {waOpen && <InvoiceWaSend invoice={doc} onClose={() => setWaOpen(false)} />}
      </div>
    </div>
  );
}

// ── Buat invoice ─────────────────────────────────────────────────────────────
// An invoice bills ONLY unpaid money (server, FIFO = Sisa Bon). Scope: seluruh tunggakan · periode ·
// pilih transaksi (open bon lines, showing what is still open of each). The footer is the server's own
// preview, so what the button creates is what the footer says.
const DOC_KIND_LABEL = { jual: 'doc.kJual', arsip: 'doc.kJual', ganti_rugi: 'doc.kGanti', saldo_awal: 'doc.kSaldo', tunggakan_lama: 'doc.kLama' };   // an imported archive bon is a refill too
function InvoiceBuilder({ customer, onClose, onCreated, canCustomize = false }) {
  const [scope, setScope] = uSdc('unpaidBon');
  const [dateFrom, setDateFrom] = uSdc('');
  const [dateTo, setDateTo] = uSdc('');
  const [picked, setPicked] = uSdc(() => new Set());
  const [dueDate, setDueDate] = uSdc('');
  const [poNumber, setPoNumber] = uSdc('');
  const [note, setNote] = uSdc('');
  const [optsOpen, setOptsOpen] = uSdc(false);
  const [opts, setOpts] = uSdc(() => DOCRENDER.mergeDocOptions({}));
  const [prev, setPrev] = uSdc(null);          // { items, total, selectable } | null while loading
  const [prevErr, setPrevErr] = uSdc('');
  const [saving, setSaving] = uSdc(false);
  const [err, setErr] = uSdc('');
  const today = (window.FIN && FIN.TODAY) || new Date().toISOString().slice(0, 10);
  uEdc(() => { const o = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', o); return () => window.removeEventListener('keydown', o); }, []);
  uEdc(() => {
    let live = true; setPrev(null); setPrevErr('');
    const qs = ['scope=' + scope];
    if (scope === 'period') { if (dateFrom) qs.push('dateFrom=' + dateFrom); if (dateTo) qs.push('dateTo=' + dateTo); }
    const h = setTimeout(() => {
      window.API.distribusi.invoices.preview(customer.id, qs.join('&'))
        .then((r) => { if (live) setPrev(r.data || { items: [], total: 0, selectable: [] }); })
        .catch((e) => { if (live) { setPrev({ items: [], total: 0, selectable: [] }); setPrevErr(docErrMsg(e, 'dist.loadErr')); } });
    }, 300);
    return () => { live = false; clearTimeout(h); };
  }, [scope, dateFrom, dateTo]);
  const selectable = (prev && prev.selectable) || [];
  const pickedRows = selectable.filter((l) => picked.has(l.txnId));
  const count = scope === 'selected' ? pickedRows.length : ((prev && prev.items) || []).length;
  const total = scope === 'selected' ? pickedRows.reduce((s, l) => s + l.remaining, 0) : ((prev && prev.total) || 0);
  // Pilih transaksi: a payment without a chosen bon pays the bons NOT picked, so the pick may not pass Sisa Bon.
  const overSisa = scope === 'selected' && prev && prev.sisaBon != null && total > prev.sisaBon;
  const toggle = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const optsChanged = JSON.stringify(opts) !== JSON.stringify(DOCRENDER.mergeDocOptions({}));
  const create = () => {
    if (saving || !count) return;
    setSaving(true); setErr('');
    const body = { scope, dueDate: dueDate || '', poNumber: poNumber.trim(), note: note.trim() };
    if (scope === 'period') { if (dateFrom) body.dateFrom = dateFrom; if (dateTo) body.dateTo = dateTo; }
    if (scope === 'selected') body.transactionIds = pickedRows.map((l) => l.txnId);
    if (canCustomize && optsChanged) body.options = opts;
    window.API.distribusi.invoices.create(customer.id, body)
      .then((r) => { setSaving(false); onCreated(r.data); })
      .catch((e) => { setSaving(false); setErr(docErrMsg(e, 'dist.loadErr')); });
  };
  const scopes = [['unpaidBon', 'doc.scopeAll'], ['period', 'doc.scopePeriod'], ['selected', 'doc.scopePick']];
  return (
    <div className="modal-scrim" onClick={onClose} style={{ zIndex: 200 }}>
      <div className="modal-card doc-builder" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><div><div style={{ fontSize: 17, fontWeight: 800 }}>{trD('dist.makeInvoice')}</div><div style={{ fontSize: 12.5, color: 'var(--text-mut)', marginTop: 3 }}>{customer.name}</div></div><button className="jp-icon doc-x" onClick={onClose}><IconClose s={18} /></button></div>
        <div className="modal-body">
          <label className="fld-label" style={{ marginTop: 0 }}>{trD('dist.invScope')}</label>
          <div className="cat-chips">{scopes.map(([k, l]) => <button key={k} type="button" className={`cat-chip ${scope === k ? 'on' : ''}`} onClick={() => setScope(k)}>{trD(l)}</button>)}</div>
          {scope === 'period' && (
            <div className="dist-form-row" style={{ marginTop: 10 }}>
              <div style={{ flex: 1 }}><label className="fld-label">{trD('dist.from')}</label><DP.DateField value={dateFrom} onChange={setDateFrom} max={dateTo || today} /></div>
              <div style={{ flex: 1 }}><label className="fld-label">{trD('dist.to')}</label><DP.DateField value={dateTo} onChange={setDateTo} min={dateFrom || undefined} max={today} /></div>
            </div>
          )}
          {scope === 'selected' && (
            <div className="doc-picklist">
              {!prev ? <div className="doc-pick-empty">{trD('doc.calculating')}</div>
                : selectable.length === 0 ? <div className="doc-pick-empty">{trD('doc.noneOpen')}</div>
                : selectable.map((l) => (
                  <label key={l.txnId} className={'doc-pick' + (picked.has(l.txnId) ? ' on' : '')}>
                    <input type="checkbox" checked={picked.has(l.txnId)} onChange={() => toggle(l.txnId)} />
                    <span className="doc-pick-l"><b>{trD(DOC_KIND_LABEL[l.kind] || 'doc.kJual')}</b><small>{fmtDateShort(l.date)}</small></span>
                    <span className="doc-pick-r tnum">{rpFull(l.remaining)}{l.remaining < l.original ? <small>{trD('doc.remainOf', { r: rpFull(l.remaining), o: rpFull(l.original) })}</small> : null}</span>
                  </label>
                ))}
            </div>
          )}
          <div className="dist-form-row">
            <div style={{ flex: 1 }}><label className="fld-label">{trD('dist.dueDate')}</label><DP.DateField value={dueDate} onChange={setDueDate} min={today} allowFuture placeholder={trD('dist.dueDate')} /></div>
            <div style={{ flex: 1 }}><label className="fld-label">{trD('doc.po')}</label><input className="fld" maxLength={60} value={poNumber} onChange={(e) => setPoNumber(e.target.value.slice(0, 60))} placeholder={trD('dist.notePh')} /></div>
          </div>
          <label className="fld-label">{trD('dist.note')}</label>
          <input className="fld" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={trD('dist.notePh')} />
          {canCustomize && (
            <div className="doc-collapse">
              <button type="button" className="doc-collapse-h" aria-expanded={optsOpen} onClick={() => setOptsOpen((v) => !v)}><span>{trD('doc.optsTitle')}{optsChanged ? <i className="doc-dot" /> : null}</span><IconCaret s={14} /></button>
              {optsOpen && <DocOptionsFields value={opts} onChange={setOpts} docType="invoice" />}
            </div>
          )}
          <div className="dist-lockrow" style={{ marginTop: 14 }}><span className="dist-lockrow-l"><IconInvoice s={14} />{prev || scope === 'selected' ? trD('doc.summary', { n: count }) : trD('doc.calculating')}</span><span className="dist-lockrow-r tnum">{rpFull(total)}</span></div>
          {prev && count === 0 && !err && !prevErr && <div className="dist-hint" style={{ marginTop: 8 }}><IconWarn s={12} />{trD(scope === 'selected' ? 'doc.pickSome' : 'doc.noneBillable')}</div>}
          {overSisa && <div className="add-err" style={{ marginTop: 8 }}><IconWarn s={14} />{trD('doc.overSisa', { sisa: rpFull(prev.sisaBon) })}</div>}
          {(err || prevErr) && <div className="add-err" style={{ marginTop: 8 }}><IconClose s={14} />{err || prevErr}</div>}
        </div>
        <div className="modal-foot"><button className="btn btn-ghost" onClick={onClose}>{trD('dist.cancel')}</button><button className="btn btn-primary" disabled={!count || saving || overSisa || (!prev && scope !== 'selected')} onClick={create}>{saving ? '…' : trD('dist.invCreate')}</button></div>
      </div>
    </div>
  );
}

// ── Buat nota ────────────────────────────────────────────────────────────────
// A nota covers ONLY the chosen transactions (never the customer's whole sisa bon). Sales and ganti rugi
// make a nota; bon payments make a separate payment receipt (kwitansi) — the two never mix. The server
// keys a nota by its transactions, so the same choice reopens the same NOTA- number.
const docTxnLabel = (t) => trD(t.kind === 'ganti_rugi' ? 'pc.ketGantiRugi' : t.method === 'pelunasan' ? 'pc.ketBayar' : t.method === 'bon' ? 'pc.ketBeliBon' : 'pc.ketBeliLunas');
const docTxnQty = (t) => (t.kind === 'ganti_rugi' ? t.gallonQty : t.qty) || 0;   // ganti rugi counts broken gallons
const docTxnAmount = (t) => (t.effectiveAmount != null ? t.effectiveAmount : t.amount) || 0;
function NotaBuilder({ customer, initialIds, onClose, onCreated, canCustomize = false }) {
  const rows = React.useMemo(() => (customer.transactions || [])
    .filter((t) => t && t.id && t.status !== 'void' && !t.voided && (t.method === 'lunas' || t.method === 'bon' || t.method === 'pelunasan'))
    .slice().sort((a, b) => (a.txnDate < b.txnDate ? 1 : a.txnDate > b.txnDate ? -1 : 0)), [customer]);
  const [picked, setPicked] = uSdc(() => new Set((initialIds || []).filter((id) => rows.some((t) => t.id === id))));
  const [optsOpen, setOptsOpen] = uSdc(false);
  const [opts, setOpts] = uSdc(() => DOCRENDER.mergeDocOptions({}));
  const [saving, setSaving] = uSdc(false);
  const [err, setErr] = uSdc('');
  uEdc(() => { const o = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', o); return () => window.removeEventListener('keydown', o); }, []);
  const sel = rows.filter((t) => picked.has(t.id));
  const payMode = sel.length ? sel[0].method === 'pelunasan' : null;   // null = nothing chosen yet
  const blocked = (t) => payMode != null && (t.method === 'pelunasan') !== payMode;
  const total = sel.reduce((s, t) => s + docTxnAmount(t), 0);
  const toggle = (t) => { if (blocked(t)) return; setPicked((cur) => { const n = new Set(cur); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n; }); };
  const optsChanged = JSON.stringify(opts) !== JSON.stringify(DOCRENDER.mergeDocOptions({}));
  const create = () => {
    if (saving || !sel.length) return;
    setSaving(true); setErr('');
    const body = { transactionIds: sel.map((t) => t.id) };
    if (canCustomize && optsChanged) body.options = opts;
    window.API.distribusi.invoices.createNota(customer.id, body)
      .then((r) => { setSaving(false); onCreated(r.data); })
      .catch((e) => { setSaving(false); setErr(docErrMsg(e, 'dist.loadErr')); });
  };
  return (
    <div className="modal-scrim" onClick={onClose} style={{ zIndex: 200 }}>
      <div className="modal-card doc-builder" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><div><div style={{ fontSize: 17, fontWeight: 800 }}>{trD('doc.makeNota')}</div><div style={{ fontSize: 12.5, color: 'var(--text-mut)', marginTop: 3 }}>{customer.name}</div></div><button className="jp-icon doc-x" onClick={onClose}><IconClose s={18} /></button></div>
        <div className="modal-body">
          <label className="fld-label" style={{ marginTop: 0 }}>{trD('doc.notaPick')}</label>
          <div className="doc-picklist">
            {rows.length === 0 ? <div className="doc-pick-empty">{trD('doc.noTxn')}</div> : rows.map((t) => (
              <label key={t.id} className={'doc-pick' + (picked.has(t.id) ? ' on' : '') + (blocked(t) ? ' off' : '')}>
                <input type="checkbox" checked={picked.has(t.id)} disabled={blocked(t)} onChange={() => toggle(t)} />
                <span className="doc-pick-l"><b>{docTxnLabel(t)}</b><small>{fmtDateShort(t.txnDate)}{t.method !== 'pelunasan' && docTxnQty(t) ? ' · ' + numX(docTxnQty(t)) + ' ' + trD('dist.galonUnit') : ''}</small></span>
                <span className="doc-pick-r tnum">{rpFull(docTxnAmount(t))}</span>
              </label>
            ))}
          </div>
          {payMode != null && <div className="dist-hint" style={{ marginTop: 8 }}><IconWarn s={12} />{trD('doc.notaPayHint')}</div>}
          {canCustomize && (
            <div className="doc-collapse">
              <button type="button" className="doc-collapse-h" aria-expanded={optsOpen} onClick={() => setOptsOpen((v) => !v)}><span>{trD('doc.optsTitle')}{optsChanged ? <i className="doc-dot" /> : null}</span><IconCaret s={14} /></button>
              {optsOpen && <DocOptionsFields value={opts} onChange={setOpts} docType="nota" />}
            </div>
          )}
          <div className="dist-lockrow" style={{ marginTop: 14 }}><span className="dist-lockrow-l"><IconInvoice s={14} />{trD('doc.summary', { n: sel.length })}</span><span className="dist-lockrow-r tnum">{rpFull(total)}</span></div>
          {err && <div className="add-err" style={{ marginTop: 8 }}><IconClose s={14} />{err}</div>}
        </div>
        <div className="modal-foot"><button className="btn btn-ghost" onClick={onClose}>{trD('dist.cancel')}</button><button className="btn btn-primary" disabled={!sel.length || saving} onClick={create}>{saving ? '…' : trD('doc.makeNota')}</button></div>
      </div>
    </div>
  );
}

// "Cetak nota" on one transaction row: the stored nota for exactly that transaction (the same
// transaction always returns the same nota — server idempotency), then the viewer.
const openNotaFor = (txn, { onOpen, onError } = {}) => window.API.distribusi.invoices.createNota(txn.customerId, { transactionIds: [txn.id] })
  .then((r) => { if (onOpen) onOpen(r.data); })
  .catch((e) => { if (onError) onError(docErrMsg(e, 'dist.loadErr')); });

// ── Desain Invoice & Nota (Pengaturan) ──────────────────────────────────────
// Form on the left, the live preview on the right: the sample documents rendered by the SAME renderer
// with the draft settings, the section being edited outlined. Signature & stamp are their own
// permission (distribusiDokumenTtd); the rest is distribusiDokumenDesain. Simpan sends only the
// sections this user may edit AND changed; the server re-checks each section and audits the save.
const DOC_IMG_MAX = 500 * 1024;
const DOC_IMG_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const DOCSET_TABS = [
  { k: 'identity', l: 'docset.tabIdentity', cap: 'design', hl: '.hd .biz' },
  { k: 'signature', l: 'docset.tabSign', cap: 'sign', hl: '.sign' },
  { k: 'payment', l: 'docset.tabPay', cap: 'design', hl: '.gl.pay' },
  { k: 'content', l: 'docset.tabContent', cap: 'design', hl: '.kind h4, .notes > div:first-child, .foot' },
  { k: 'paper', l: 'docset.tabPaper', cap: 'design', hl: '.pg' },
];
const docClone = (x) => JSON.parse(JSON.stringify(x));

function DocImageField({ label, value, onChange, hint }) {
  const ref = uRdc(null);
  const [err, setErr] = uSdc('');
  const pick = (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = '';
    if (!f) return;
    if (DOC_IMG_TYPES.indexOf(f.type) < 0 || f.size > DOC_IMG_MAX) { setErr(trD('docset.imgErr')); return; }
    setErr('');
    readDataURL(f).then(onChange).catch(() => setErr(trD('docset.imgErr')));   // no re-encode: keeps PNG transparency
  };
  return (
    <div className="docset-fld">
      <div className="docset-lbl">{label}</div>
      <div className={'docset-img' + (value ? ' on' : '')}>
        <span className="docset-img-th">{value ? <img src={value} alt="" /> : <IconDownload s={16} />}</span>
        <span className="docset-img-t"><b>{value ? trD('docset.imgSet') : trD('docset.imgNone')}</b><small>{hint || trD('docset.imgHint')}</small></span>
        <span className="docset-img-a">
          <button type="button" className="dist-link" onClick={() => ref.current && ref.current.click()}>{value ? trD('docset.imgChange') : trD('docset.imgPick')}</button>
          {value ? <button type="button" className="dist-link" style={{ color: 'var(--neg)' }} onClick={() => onChange('')}>{trD('docset.imgRemove')}</button> : null}
        </span>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pick} />
      </div>
      {err && <div className="add-err" style={{ marginTop: 6 }}><IconWarn s={13} />{err}</div>}
    </div>
  );
}
function DocToggleRow({ label, sub, checked, onChange }) {
  return (
    <label className="docset-tgl">
      <span><b>{label}</b>{sub ? <small>{sub}</small> : null}</span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="doc-switch-ui" aria-hidden="true" />
    </label>
  );
}
function DocText({ label, value, onChange, max, area, ph }) {
  return (
    <div className="docset-fld">
      <label className="docset-lbl">{label}</label>
      {area ? <textarea className="fld" rows={3} maxLength={max} value={value || ''} placeholder={ph} onChange={(e) => onChange(e.target.value)} />
        : <input className="fld" maxLength={max} value={value || ''} placeholder={ph} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

function DocDesignScreen({ canDesign, canSign, onClose }) {
  const [saved, setSaved] = uSdc(null);       // what the server holds
  const [draft, setDraft] = uSdc(null);
  const [tab, setTab] = uSdc(canDesign ? 'identity' : 'signature');
  const [applyTo, setApplyTo] = uSdc('invoice');   // invoice | nota | both
  const [hist, setHist] = uSdc(null);
  const [histOpen, setHistOpen] = uSdc(false);
  const [prevOpen, setPrevOpen] = uSdc(true);
  const [busy, setBusy] = uSdc(false);
  const [err, setErr] = uSdc('');
  const [toast, setToast] = uSdc('');
  const mayEdit = (cap) => (cap === 'sign' ? canSign : canDesign);
  const tabs = DOCSET_TABS.filter((t) => mayEdit(t.cap));
  const refreshHist = () => window.API.distribusi.docSettings.history().then((r) => setHist(r.data || [])).catch(() => setHist([]));
  uEdc(() => {
    window.API.distribusi.docSettings.get().then((r) => {
      const s = DOCRENDER.mergeDocSettings(r && r.data);
      setSaved(s); setDraft(docClone(s)); setApplyTo(s.content.sameForBoth ? 'both' : 'invoice');
    }).catch((e) => setErr(docErrMsg(e, 'dist.loadErr')));
    refreshHist();
    const o = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', o); return () => window.removeEventListener('keydown', o);
  }, []);
  if (!draft) {
    return (<div className="docset-screen"><div className="doc-loading">{err ? <><IconWarn s={14} />{err}</> : <><IconClock s={14} />{trD('doc.loading')}</>}</div></div>);
  }
  const set = (sec, patch) => setDraft((d) => ({ ...d, [sec]: { ...d[sec], ...patch } }));
  const ctype = applyTo === 'nota' ? 'nota' : 'invoice';         // which content block the form edits
  const ptype = applyTo === 'nota' ? 'nota' : 'invoice';         // which sample the preview shows
  const content = draft.content[ctype];
  const setContent = (patch) => setDraft((d) => ({ ...d, content: { ...d.content, [ctype]: { ...d.content[ctype], ...patch } } }));
  const setShow = (k, v) => setContent({ show: { ...content.show, [k]: v } });
  const pickApply = (v) => { setApplyTo(v); setDraft((d) => ({ ...d, content: { ...d.content, sameForBoth: v === 'both' } })); };
  const sections = DOCSET_TABS.filter((t) => mayEdit(t.cap)).map((t) => t.k)
    .filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(saved[k]));
  const save = () => {
    if (busy || !sections.length) return;
    const patch = {}; sections.forEach((k) => { patch[k] = draft[k]; });
    setBusy(true); setErr('');
    window.API.distribusi.docSettings.save(patch)
      .then((r) => {
        const s = DOCRENDER.mergeDocSettings(r && r.data); setSaved(s); setDraft(docClone(s));
        loadDocSettings(true); refreshHist();
        setToast(trD('docset.saved')); setTimeout(() => setToast(''), 2600);
      })
      .catch((e) => setErr(docErrMsg(e)))
      .finally(() => setBusy(false));
  };
  const resetTab = () => setDraft((d) => ({ ...d, [tab]: docClone(DOCRENDER.DEFAULT_DOC_SETTINGS[tab]) }));
  const banks = draft.payment.banks || [];
  const setBank = (i, patch) => set('payment', { banks: banks.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  const last = hist && hist[0];
  const cur = DOCSET_TABS.find((t) => t.k === tab) || DOCSET_TABS[0];
  const sample = DOCRENDER.SAMPLE_DOCS[ptype];
  const paperKeys = Object.keys(DOCRENDER.PAPERS);
  const paperLabel = (k, type) => DOCRENDER.PAPERS[k].label + ((type === 'invoice' && k === 'a4') || (type === 'nota' && k === 'a5') ? ' ' + trD('doc.recommended') : '');

  return (
    <div className="docset-screen">
      <div className="docset-card">
        <div className="docset-head">
          <div className="docset-head-t"><b>{trD('docset.title')}</b><span>{last ? trD('docset.lastBy', { who: last.actorName || '—', at: fmtDT(last.createdAt ? new Date(last.createdAt).toISOString() : '') }) : trD('docset.never')}</span></div>
          <div className="docset-head-a">
            <button type="button" className="btn btn-ghost" onClick={() => setHistOpen((v) => !v)}><IconClock s={15} />{trD('docset.history')}</button>
            <button type="button" className="btn btn-ghost" onClick={resetTab}><IconRefresh s={15} />{trD('docset.reset')}</button>
            <button type="button" className="btn btn-primary" disabled={busy || !sections.length} onClick={save}><IconCheck s={15} />{busy ? '…' : trD('docset.save')}</button>
          </div>
          <button type="button" className="jp-icon doc-close" aria-label={trD('doc.close')} onClick={onClose}><IconClose s={18} /></button>
        </div>
        {err && <div className="dist-warnbox docset-err"><IconWarn s={14} /><span>{err}</span></div>}
        {histOpen && (
          <div className="docset-hist">
            {!hist ? <div className="cd-muted">{trD('doc.loading')}</div> : hist.length === 0 ? <div className="cd-muted">{trD('docset.histNone')}</div> : hist.map((h) => (
              <div key={h.id} className="docset-hist-row"><b>{h.title}</b><span>{h.detail}</span><small>{(h.actorName || '—') + ' · ' + fmtDT(h.createdAt ? new Date(h.createdAt).toISOString() : '')}</small></div>
            ))}
          </div>
        )}
        <div className="docset-body">
          <div className="docset-form">
            <div className="docset-tabs" role="tablist">{tabs.map((t) => <button key={t.k} type="button" role="tab" aria-selected={tab === t.k} className={'docset-tab' + (tab === t.k ? ' on' : '')} onClick={() => setTab(t.k)}>{trD(t.l)}</button>)}</div>
            <div className="docset-apply"><span>{trD('docset.applyTo')}</span>{[['invoice', 'docset.apInvoice'], ['nota', 'docset.apNota'], ['both', 'docset.apBoth']].map(([k, l]) => <button key={k} type="button" className={'cat-chip' + (applyTo === k ? ' on' : '')} onClick={() => pickApply(k)}>{trD(l)}</button>)}</div>
            <div className="docset-fields">
              {tab === 'identity' && (<>
                <DocImageField label={trD('docset.logo')} value={draft.identity.logo} onChange={(v) => set('identity', { logo: v })} />
                <DocText label={trD('docset.bizName')} max={80} value={draft.identity.bizName} onChange={(v) => set('identity', { bizName: v })} />
                <DocText label={trD('docset.company')} max={80} value={draft.identity.company} onChange={(v) => set('identity', { company: v })} />
                <DocText label={trD('docset.address')} max={200} area value={draft.identity.address} onChange={(v) => set('identity', { address: v })} />
                <div className="docset-two">
                  <DocText label={trD('docset.phone')} max={40} value={draft.identity.phone} onChange={(v) => set('identity', { phone: v })} />
                  <DocText label={trD('docset.email')} max={80} value={draft.identity.email} onChange={(v) => set('identity', { email: v })} />
                </div>
                <DocText label={trD('docset.npwp')} max={30} value={draft.identity.npwp} onChange={(v) => set('identity', { npwp: v })} ph={trD('dist.notePh')} />
              </>)}
              {tab === 'signature' && canSign && (<>
                <div className="docset-sub">{trD('docset.signer')}</div>
                <div className="docset-two">
                  <DocText label={trD('docset.signName')} max={60} value={draft.signature.name} onChange={(v) => set('signature', { name: v })} />
                  <DocText label={trD('docset.signTitle')} max={60} value={draft.signature.title} onChange={(v) => set('signature', { title: v })} />
                </div>
                <DocText label={trD('docset.city')} max={40} value={draft.signature.city} onChange={(v) => set('signature', { city: v })} />
                <DocToggleRow label={trD('docset.showCityDate')} checked={draft.signature.showCityDate} onChange={(v) => set('signature', { showCityDate: v })} />
                <DocImageField label={trD('docset.signImage')} hint={trD('docset.pngHint')} value={draft.signature.signImage} onChange={(v) => set('signature', { signImage: v })} />
                <DocImageField label={trD('docset.stampImage')} hint={trD('docset.pngHint')} value={draft.signature.stampImage} onChange={(v) => set('signature', { stampImage: v })} />
                <DocToggleRow label={trD('docset.showSign')} sub={trD('docset.showSignSub')} checked={draft.signature.showSign} onChange={(v) => set('signature', { showSign: v })} />
                <DocToggleRow label={trD('docset.showStamp')} sub={trD('docset.showStampSub')} checked={draft.signature.showStamp} onChange={(v) => set('signature', { showStamp: v })} />
                <div className="docset-note"><IconWarn s={14} /><span>{trD('docset.signWarn')}</span></div>
              </>)}
              {tab === 'payment' && (<>
                {banks.map((b, i) => (
                  <div key={i} className="docset-bank">
                    <div className="docset-bank-h"><span>{trD('docset.bankN', { n: i + 1 })}</span><button type="button" className="dist-link" style={{ color: 'var(--neg)' }} onClick={() => set('payment', { banks: banks.filter((x, j) => j !== i) })}><IconTrash s={12} />{trD('docset.bankRemove')}</button></div>
                    <div className="docset-two">
                      <DocText label={trD('docset.bank')} max={30} value={b.bank} onChange={(v) => setBank(i, { bank: v })} ph="BCA" />
                      <DocText label={trD('docset.account')} max={40} value={b.account} onChange={(v) => setBank(i, { account: v })} />
                    </div>
                    <DocText label={trD('docset.holder')} max={80} value={b.holder} onChange={(v) => setBank(i, { holder: v })} />
                  </div>
                ))}
                {banks.length < 4 && <button type="button" className="btn btn-ghost btn-sm" onClick={() => set('payment', { banks: banks.concat([{ bank: '', account: '', holder: '' }]) })}><IconPlus s={13} />{trD('docset.bankAdd')}</button>}
                <DocImageField label={trD('docset.qris')} value={draft.payment.qris} onChange={(v) => set('payment', { qris: v })} />
              </>)}
              {tab === 'content' && (<>
                <DocText label={trD('docset.docTitle')} max={30} value={content.title} onChange={(v) => setContent({ title: v })} />
                <DocText label={trD('docset.note')} max={500} area value={content.note} onChange={(v) => setContent({ note: v })} ph={trD('dist.notePh')} />
                <DocText label={trD('docset.closing')} max={120} value={content.closing} onChange={(v) => setContent({ closing: v })} />
                <div className="docset-sub">{trD('docset.showParts')}</div>
                {[['po', 'docset.showPo'], ['due', 'docset.showDue'], ['terbilang', 'docset.showTerbilang'], ['qris', 'docset.showQris'], ['sign', 'docset.showSignBlock']].map(([k, l]) => (
                  <DocToggleRow key={k} label={trD(l)} checked={content.show[k]} onChange={(v) => setShow(k, v)} />
                ))}
              </>)}
              {tab === 'paper' && (<>
                {['invoice', 'nota'].map((type) => (
                  <div key={type} className="docset-fld">
                    <label className="docset-lbl">{trD(type === 'nota' ? 'docset.paperNota' : 'docset.paperInvoice')}</label>
                    <select className="fld" value={draft.paper[type]} onChange={(e) => set('paper', { [type]: e.target.value })}>
                      {paperKeys.map((k) => <option key={k} value={k}>{paperLabel(k, type)}</option>)}
                    </select>
                  </div>
                ))}
                <div className="docset-two">
                  <div className="docset-fld"><label className="docset-lbl">{trD('docset.margin')}</label><input className="fld" type="number" min={5} max={25} step={1} value={draft.paper.marginMm} onChange={(e) => set('paper', { marginMm: Math.min(25, Math.max(5, Math.round(+e.target.value || 12))) })} /></div>
                  <div className="docset-fld"><label className="docset-lbl">{trD('docset.fontScale')}</label><input className="fld" type="number" min={85} max={120} step={5} value={Math.round(draft.paper.fontScale * 100)} onChange={(e) => set('paper', { fontScale: Math.min(120, Math.max(85, Math.round(+e.target.value || 100))) / 100 })} /></div>
                </div>
              </>)}
            </div>
          </div>
          <div className="docset-preview">
            <button type="button" className="docset-prev-toggle" aria-expanded={prevOpen} onClick={() => setPrevOpen((v) => !v)}><span>{trD('docset.preview')}</span><IconCaret s={14} /></button>
            {prevOpen && (<>
              <div className="docset-papers"><span>{trD('docset.paper')}</span>{paperKeys.map((k) => (
                <button key={k} type="button" className={'cat-chip' + (draft.paper[ptype] === k ? ' on' : '')} disabled={!canDesign} onClick={() => set('paper', { [ptype]: k })}>{paperLabel(k, ptype)}</button>
              ))}</div>
              <DocFrame doc={sample} settings={draft} highlight={cur.hl} />
              <div className="docset-legend"><i />{trD('docset.legend')}</div>
            </>)}
          </div>
        </div>
        {toast && <div className="wa-toast docset-toast"><IconCheck s={13} />{toast}</div>}
      </div>
    </div>
  );
}
